import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Loader2, Download, Sun } from "lucide-react";
import { Button } from "../components/ui/button";
import LeadOutlook from "../components/LeadOutlook";

function PdfInstallerLogo({ url, name }) {
  const [ok, setOk] = useState(!!url);
  if (url && ok) {
    return <img src={url} alt={name} onError={() => setOk(false)} className="h-24 w-24 sm:h-[146px] sm:w-[146px] rounded-lg object-contain bg-white border border-neutral-200 shrink-0" />;
  }
  return <div className="h-[73px] w-[73px] rounded-lg bg-neutral-100 text-neutral-800 flex items-center justify-center font-heading font-extrabold text-lg shrink-0">{(name || "?").charAt(0).toUpperCase()}</div>;
}

export default function OutlookPrint({ publicMode = false, consulenzaMode = false, contractMode: contractModeProp = false }) {
  const { id, token } = useParams();
  const pdfExport = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("pdf") === "1";
  const contractMode = contractModeProp || (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("contract") === "1");
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [docHash, setDocHash] = useState(null);
  const [rete, setRete] = useState(null);
  const [pdfReadyUrl, setPdfReadyUrl] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    document.title = contractMode ? "Contratto · Stampa" : (consulenzaMode ? "Analisi energetica · Stampa" : "Proposta · Stampa");
    if (publicMode) {
      const url = consulenzaMode ? `/public/consulenza/${token}` : `/public/outlook/${token}`;
      api.get(url).then((r) => { setLead({ name: r.data?.cliente?.name || "cliente", _public: true }); setRete(r.data?.rete || null); }).catch(() => {}).finally(() => setLoading(false));
    } else {
      api.get(`/leads/${id}`).then((r) => setLead(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [id, token, publicMode, consulenzaMode, contractMode]);

  useEffect(() => {
    if (!contractMode) return;
    const url = publicMode ? `/public/outlook/${token}/contratto-firmato` : `/leads/${id}/contratto-firmato`;
    api.get(url).then((r) => setDocHash(r.data?.document_hash || null)).catch(() => {});
  }, [contractMode, publicMode, token, id]);

  const generatePdf = async () => {
    // Generazione PDF ASINCRONA: crea un job lato server e fa polling dello stato.
    // NON apre alcuna scheda/pagina: quando il PDF è pronto compare il pulsante "Apri il PDF".
    const t = token || lead?.outlook_token;
    if (!t) {
      toast.error("Condividi prima la proposta per generare il PDF.");
      return;
    }
    const kind = contractMode ? "contratto" : (consulenzaMode ? "consulenza" : "outlook");
    const base = process.env.REACT_APP_BACKEND_URL;
    setPdfReadyUrl(null);
    setPdfLoading(true);
    setCountdown(0);
    const timer = setInterval(() => setCountdown((c) => c + 1), 1000);
    try {
      const { data: job } = await api.post(`/public/print-pdf-job/${t}?kind=${kind}`);
      const jobId = job?.job_id;
      if (!jobId) throw new Error("Impossibile avviare la generazione del PDF.");
      let status = "pending", err = null, tries = 0;
      while (status === "pending" && tries < 120) { // fino a ~240s
        await new Promise((r) => setTimeout(r, 2000));
        tries += 1;
        try {
          const { data: st } = await api.get(`/public/print-pdf-status/${jobId}`);
          status = st?.status || "pending";
          err = st?.error;
        } catch (_) { /* riprova al giro successivo */ }
      }
      if (status !== "done") throw new Error(err || "Generazione PDF non riuscita (timeout).");
      // Nessuna apertura automatica: mostro il pulsante "Apri il PDF" (tap utente = apertura sicura).
      setPdfReadyUrl(`${base}/api/public/print-pdf-result/${jobId}`);
      toast.success("PDF pronto: tocca \u201cApri il PDF\u201d per visualizzarlo.");
    } catch (e) {
      startedRef.current = false; // consente un nuovo tentativo manuale
      toast.error(e?.response?.data?.detail || e?.message || "Generazione PDF non riuscita.");
    } finally {
      clearInterval(timer);
      setPdfLoading(false);
      setCountdown(0);
    }
  };

  // Avvio AUTOMATICO della generazione all'apertura della pagina di stampa, così il PDF
  // è pronto in anticipo. Non apre nulla: l'utente lo apre col pulsante quando è pronto.
  useEffect(() => {
    if (loading || !lead) return;
    if (pdfExport) return; // NON auto-generare quando la pagina è renderizzata DAL motore PDF (evita loop infinito)
    const t = token || lead?.outlook_token;
    if (!t || startedRef.current) return;
    startedRef.current = true;
    generatePdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, lead, token]);

  if (loading) return <div className="flex h-screen items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-[#C99700]" /></div>;
  if (!lead) return <div className="flex h-screen items-center justify-center bg-white text-neutral-700">Proposta non disponibile.</div>;

  return (
    <div className="print-surface min-h-screen bg-white">
      {/* Barra azioni (non stampata) */}
      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2">
          <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-9 w-9 rounded-full object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-sm tracking-wide text-neutral-900">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-[0.15em]">Div. Chiavi in mano</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pdfReadyUrl && !pdfLoading ? (
            <Button asChild className="font-semibold bg-green-600 text-white hover:bg-green-700" data-testid="download-pdf-btn">
              <a href={pdfReadyUrl} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-2" /> Apri il PDF
              </a>
            </Button>
          ) : (
            <Button onClick={generatePdf} disabled={pdfLoading} className="font-semibold bg-[#FDD003] text-black hover:bg-[#e6d600]" data-testid="download-pdf-btn">
              {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {pdfLoading ? `Attendere… ${countdown}s` : "Genera PDF"}
            </Button>
          )}
        </div>
      </div>

      {pdfLoading && (
        <div className="no-print flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 sm:px-6 py-2 text-sm text-amber-800" data-testid="pdf-wait-notice">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span>Generazione del PDF in corso, attendere senza chiudere la pagina… <span className="font-semibold">({countdown}s)</span></span>
        </div>
      )}

      {pdfReadyUrl && !pdfLoading && (
        <div className="no-print flex flex-wrap items-center justify-between gap-2 border-b border-green-200 bg-green-50 px-4 sm:px-6 py-2.5 text-sm text-green-800" data-testid="pdf-ready-notice">
          <span className="font-medium">Il PDF è pronto.</span>
          <Button asChild size="sm" className="font-semibold bg-green-600 text-white hover:bg-green-700" data-testid="open-pdf-ready-btn">
            <a href={pdfReadyUrl} target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4 mr-2" /> Apri il PDF
            </a>
          </Button>
        </div>
      )}

      {consulenzaMode && rete && rete.installer?.ragione_sociale && (
        rete.brand_mode === "partner" ? (
          <div className="mx-auto max-w-4xl px-4 sm:px-6 pt-6 flex items-start justify-between gap-3 sm:gap-4" data-testid="pdf-partner-brand">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <PdfInstallerLogo url={rete.installer?.logo_url} name={rete.installer?.ragione_sociale} />
              <div className="leading-tight min-w-0 flex-1">
                <div className="font-heading font-extrabold text-base text-neutral-900 break-words">{rete.installer?.ragione_sociale}</div>
                {rete.installer?.indirizzo && <div className="text-[11px] text-neutral-500">{rete.installer.indirizzo}</div>}
                {[rete.installer?.citta, rete.installer?.provincia].filter(Boolean).length > 0 && (
                  <div className="text-[11px] text-neutral-500">{[rete.installer?.citta, rete.installer?.provincia].filter(Boolean).join(" · ")}</div>
                )}
                {rete.installer?.p_iva && <div className="text-[11px] text-neutral-500">P.IVA {rete.installer.p_iva}</div>}
                {rete.installer?.telefono && <div className="text-[11px] text-neutral-500">Tel. {rete.installer.telefono}</div>}
                {rete.installer?.email && <div className="text-[11px] text-neutral-500 break-all">{rete.installer.email}</div>}
                {rete.partner_note && <span className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wide text-[#C99700] bg-[#FDD003]/15 rounded-full px-2 py-0.5">{rete.partner_note}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0" data-testid="pdf-torri-mark">
              <div className="flex items-center gap-2">
                <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-8 w-8 sm:h-[47px] sm:w-[47px] rounded-full object-contain" />
                <div className="leading-none text-right hidden sm:block">
                  <div className="font-heading font-extrabold text-[20px] tracking-wide text-neutral-900">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-500 mt-0.5">Div. Chiavi in mano</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-4xl px-4 sm:px-6 pt-6 flex items-start justify-between gap-4" data-testid="pdf-partner-brand">
            <div className="flex items-center gap-2">
              <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-[47px] w-[47px] rounded-full object-contain" />
              <div className="leading-none">
                <div className="font-heading font-extrabold text-[20px] tracking-wide text-neutral-900">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-500 mt-0.5">Div. Chiavi in mano</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0 min-w-0">
              <span className="text-[10px] text-neutral-500 text-right" data-testid="pdf-featuring">In collaborazione con l'installatore</span>
              <div className="leading-tight text-right min-w-0">
                <div className="font-heading font-extrabold text-base text-neutral-900 break-words">{rete.installer?.ragione_sociale}</div>
                {rete.installer?.indirizzo && <div className="text-[11px] text-neutral-500">{rete.installer.indirizzo}</div>}
                {[rete.installer?.citta, rete.installer?.provincia].filter(Boolean).length > 0 && (
                  <div className="text-[11px] text-neutral-500">{[rete.installer?.citta, rete.installer?.provincia].filter(Boolean).join(" · ")}</div>
                )}
                {rete.installer?.p_iva && <div className="text-[11px] text-neutral-500">P.IVA {rete.installer.p_iva}</div>}
                {rete.installer?.telefono && <div className="text-[11px] text-neutral-500">Tel. {rete.installer.telefono}</div>}
                {rete.installer?.email && <div className="text-[11px] text-neutral-500 break-all">{rete.installer.email}</div>}
              </div>
            </div>
          </div>
        )
      )}

      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-8">
        {publicMode ? <LeadOutlook publicToken={token} printPage consulenzaMode={consulenzaMode} pdfExport={pdfExport} contractMode={contractMode} documentHash={docHash} /> : <LeadOutlook lead={lead} printPage pdfExport={pdfExport} contractMode={contractMode} documentHash={docHash} />}
      </div>
    </div>
  );
}
